import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';

export const filterSelectAttributeVariables = (
  fields: FieldMappingFieldItem[]
): FieldMappingFieldItem[] => {
  const seenBySourceAndLabel = new Set<string>();

  return fields.reduce((acc, field) => {
    if (field.type === 'select') {
      const baseLabel = field.label.replace(/\s*\((value|label)\)$/, '');
      const key = `${field.source}::${baseLabel}`;
      if (!seenBySourceAndLabel.has(key)) {
        seenBySourceAndLabel.add(key);
        acc.push({
          ...field,
          field: 'value',
          label: baseLabel,
        });
      }
    } else if (field.type === 'multi_select') {
      const baseLabel = field.label.replace(/\s*\((values|labels)\)$/, '');
      const key = `${field.source}::${baseLabel}`;
      if (!seenBySourceAndLabel.has(key)) {
        seenBySourceAndLabel.add(key);
        acc.push({
          ...field,
          field: 'values',
          label: baseLabel,
        });
      }
    } else {
      acc.push(field);
    }
    return acc;
  }, [] as FieldMappingFieldItem[]);
};

/**
 * Select attributes are exposed to the builder under two sibling discriminators
 * that point at the *same* underlying field (identical type and options): the
 * value variant (`value` / `values`) and the label variant (`label` / `labels`).
 * The backend only emits one of the pair when the options are opaque — an
 * attribute backed by a hidden, non-marketplace option list exposes only the
 * label variant — but a saved condition can reference either. When the exact
 * discriminator is no longer present we still resolve the field via its sibling
 * so the editor can determine the value type and options; otherwise the
 * comparator and value inputs fall back to a text field and render blank.
 */
const SELECT_DISCRIMINATOR_SIBLINGS: Record<string, string> = {
  value: 'label',
  label: 'value',
  values: 'labels',
  labels: 'values',
};

const SELECT_FIELD_TYPES = new Set<FieldType>(['select', 'multi_select']);

export const findMappedField = (
  formValue: VariableValue | undefined,
  variableGroups: FieldMappingGroup[]
): FieldMappingFieldItem | undefined => {
  if (!formValue || !formValue.variables) {
    return undefined;
  }
  const variable = Object.values(formValue.variables)[0];
  if (variable?.source === 'action') {
    const action = variableGroups.find((group) => group.id === variable.action);
    if (action) {
      return action.fields.find((field) => field.field === variable.field);
    }
    return undefined;
  }

  const fields = variableGroups.flatMap((obj) => obj.fields);
  const exactMatch = fields.find(
    (field) => field.source === variable?.source && field.field === variable?.field
  );
  if (exactMatch) {
    return exactMatch;
  }

  const siblingField = variable?.field ? SELECT_DISCRIMINATOR_SIBLINGS[variable.field] : undefined;
  if (siblingField) {
    return fields.find(
      (field) =>
        field.source === variable?.source &&
        field.field === siblingField &&
        SELECT_FIELD_TYPES.has(field.type as FieldType)
    );
  }

  return undefined;
};

export const findValueType = (mappedField?: FieldMappingFieldItem): string => {
  const typeMapping: Partial<Record<FieldType, string>> = {
    select: 'select',
    multi_select: 'multi_select',
    date: 'date',
    boolean: 'boolean',
    file: 'file',
    number: 'number',
    text: 'text',
    decimal: 'number',
  };

  return typeMapping[mappedField?.type ?? 'text'] ?? 'text';
};

export const findValueOptions = (mappedField?: FieldMappingFieldItem) => {
  if (mappedField?.options) {
    return mappedField.options.map((option) => {
      const label = 'display' in option ? option.display : option.label;
      const tooltip = typeof label === 'string' ? label : undefined;
      const optionValue = ('shortId' in option ? option.shortId : option.value) as string;
      return {
        value: optionValue,
        label,
        tooltip,
      };
    });
  }
  return [];
};
